'use client';
import config from '../../../config/bite-ga4.json';
import plans from '../../../config/bite-plans.json';
import {initializeConsent,loadLocalConsent} from './consent';
import {allowsBiteGa4} from './bite-ga4-policy';
import {isTestVisit} from './bite-reading-totals';
import {biteAudience,resolveBiteAudience} from './bite-conversion';
import {usagePage} from './bite-usage-totals';
import {biteGa4Attribution,clearBiteGa4Attribution} from './bite-ga4-attribution';

type GaWindow=Window & {dataLayer?:unknown[];gtag?:(...args:unknown[])=>void};
let initialized=false,lastPage='';
const events:Record<string,string>={click:'action',search:'search',login_click:'login_start',generation_start:'generation',generation_ready:'generation_ready',completion_view:'completion_view',free_report_open:'free_report_open',report_offer_view:'report_offer_view',paywall_view:'paywall',checkout_click:'checkout_click',checkout_redirect:'checkout',report_open:'report_open',card_reveal:'card_reveal'};
for(const stage of ['login_view','login_google','login_apple','login_success','login_failed','login_dismiss','checkout_request','checkout_error'])events[stage]=stage;
for(const stage of ['search_error','generation_error','checkout_cancel','collection_view','account_view','art_retry','error'])events[stage]=stage;
events.report_gift_view='report_gift_view';
export function stopBiteGa4() {
  const w=window as GaWindow;
  Reflect.set(w,`ga-disable-${config.measurementId}`,true);
  if(initialized)w.gtag?.('consent','update',{analytics_storage:'denied',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});
  lastPage='';
  identity=null;
  const choice=loadLocalConsent();
  if(choice?.ts&&!choice.analytics)clearBiteGa4Attribution();
}
async function ready() {
  await initializeConsent();
  if(!allowsBiteGa4()||await isTestVisit())return null;
  if(await resolveBiteAudience()==='unknown')return null;
  if(!allowsBiteGa4())return null;
  const w=window as GaWindow;
  Reflect.set(w,`ga-disable-${config.measurementId}`,false);
  if(!initialized){
    w.dataLayer=w.dataLayer||[];
    w.gtag=w.gtag||function(){w.dataLayer!.push(arguments);};
    w.gtag('consent','default',{analytics_storage:'granted',ad_storage:'denied',ad_user_data:'denied',ad_personalization:'denied'});
    w.gtag('js',new Date());
    w.gtag('config',config.measurementId,{send_page_view:false,allow_google_signals:false,allow_ad_personalization_signals:false,
      page_location:`https://bite.cx/${usagePage(location.pathname)||'other'}`,page_referrer:'',page_title:'Bite',...biteGa4Attribution()});
    const script=document.createElement('script');script.async=true;script.id='bite-ga4';
    script.src=`https://www.googletagmanager.com/gtag/js?id=${config.measurementId}`;
    document.head.appendChild(script);initialized=true;
  }else w.gtag?.('consent','update',{analytics_storage:'granted'});
  return w.gtag;
}
export async function trackBiteGa4(type:string,metadata:Record<string,unknown>={}) {
  const event=events[type];if(!event)return false;
  const page=usagePage(location.pathname)||'other';
  const acquisition=biteGa4Attribution();
  const send=await ready();if(!send)return false;
  const details:Record<string,string>={};
  if(type==='click'&&/^bite\.[a-z0-9_.-]{1,95}$/.test(String(metadata.label)))details.bite_action=String(metadata.label);
  if(metadata.report_offer===plans.reportOffer.id)details.report_offer=plans.reportOffer.id;
  if(['free','public','example','single','monthly','admin','locked'].includes(String(metadata.report_access)))details.report_access=String(metadata.report_access);
  if(['report_growth','report_chart','report_end','report_offer','completion_paywall','pricing','member_offer','credit_wallet'].includes(String(metadata.label)))details.checkout_placement=String(metadata.label);
  if(['card','monthly'].includes(String(metadata.plan)))details.bite_plan=String(metadata.plan);
  send('event',`bite_${biteAudience()}_${event}`,{...details,send_to:config.measurementId,transport_type:'beacon',page_location:`https://bite.cx/${page}`,page_title:'Bite',page_referrer:'',...acquisition,bite_audience:biteAudience()});
  return true;
}
export async function startBiteGa4(path:string) {
  const send=await ready();if(!send||lastPage===path)return;
  lastPage=path;
  const page=usagePage(path)||'other';
  const args={send_to:config.measurementId,page_location:`https://bite.cx/${page}`,page_title:`Bite ${page}`,page_referrer:'',...biteGa4Attribution(),bite_audience:biteAudience()};
  send('event','page_view',args);
  send('event',`bite_${biteAudience()}_${page==='landing'?'landing':'visit'}`,args);
  void biteGa4CheckoutContext();
}

// A blocked tag must never delay Checkout indefinitely or create a replacement identity.
let identity:{at:number;value:{clientId:string;sessionId:string}}|null=null;
export async function biteGa4CheckoutContext() {
  const capture=async()=>{
    const send=await ready();if(!send)return null;
    if(identity&&Date.now()-identity.at<5*60000)return {...identity.value,acquisition:biteGa4Attribution()};
    const get=(key:string)=>new Promise<unknown>(resolve=>send('get',config.measurementId,key,resolve));
    const [clientId,sessionId]=await Promise.all([get('client_id'),get('session_id')]);
    if(!allowsBiteGa4()||!/^\d{1,20}\.\d{1,20}$/.test(String(clientId))||!/^\d{1,16}$/.test(String(sessionId)))return null;
    identity={at:Date.now(),value:{clientId:String(clientId),sessionId:String(sessionId)}};
    return {...identity.value,acquisition:biteGa4Attribution()};
  };
  let timer:ReturnType<typeof setTimeout>|undefined;
  try{return await Promise.race([capture(),new Promise<null>(resolve=>{timer=setTimeout(()=>resolve(null),1500);})]);}
  catch{return null;}finally{clearTimeout(timer);}
}
